import { api } from "./client";

/** What the printer's own driver can do (sizes in micrometres). */
export type PrinterCapabilities = {
  sizes: { name: string; w: number; h: number }[];
  duplex: boolean;
  color: boolean;
  bins: string[];
};

export type PrintOptions = {
  sides?: "one-sided" | "two-sided-long-edge" | "two-sided-short-edge";
  color?: "color" | "monochrome";
  paper?: string;
  tray?: string;
};

export type PrinterRow = {
  id: string;
  printer_name: string;
  label: string;
  driver: string;
  port_name: string;
  share_name: string;
  is_shared: boolean;
  status: "detected" | "pending" | "active" | "failed" | "removing" | "removed";
  connectivity: "ready" | "offline" | "error" | "unknown" | "unplugged";
  capabilities?: PrinterCapabilities | Record<string, never>;
  // ID-card printers (Magicard and the like) can keep one saved set of
  // settings that every card printed through KraetdNet uses.
  is_card_printer?: boolean;
  card_settings_info?: CardSettingsInfo | null;
  host_agent: string;
  host_agent_hostname: string;
  host_agent_label: string;
  detected_at: string;
  created_at: string;
  updated_at: string;
};

/** What the dashboard shows about a card printer's saved settings. */
export type CardSettingsInfo = {
  saved_at?: string;
  saved_by?: string;
  source?: "user" | "printer_defaults" | "";
  summary?: {
    orientation?: "portrait" | "landscape";
    color?: "color" | "black_and_white";
    sides?: "one" | "both";
    card_size?: string;
  };
};

export async function listPrinters() {
  const { data } = await api.get<{ results: PrinterRow[] }>("/printers/");
  // Show only printers that are really there. An unplugged printer that isn't shared is
  // hidden; a shared one stays visible (labelled "Unplugged") so the office knows why it's
  // missing from the print dialog.
  return {
    ...data,
    results: data.results.filter((p) => p.connectivity !== "unplugged" || p.status === "active"),
  };
}

export async function sharePrinter(id: string) {
  const { data } = await api.post<PrinterRow>(`/printers/${id}/share`);
  return data;
}

export async function unsharePrinter(id: string) {
  await api.delete(`/printers/${id}/share`);
}

export async function unshareAllPrinters() {
  await api.delete("/printers/shared");
}

// Dashboard-only printing pivot: remove the native network printers
// KraetdNet installed on each PC (the ones that freeze the Windows
// print dialog when their host is unreachable). Printers stay shared
// for dashboard printing — only the per-PC native install is removed.
export async function uninstallPrintersFromClients() {
  const { data } = await api.post<{ uninstall_tasks_created: number }>(
    "/printers/uninstall-from-clients",
  );
  return data;
}

// Rename a printer so it's distinguishable in the dropdown when an office
// has multiple identical-model devices ("EPSON L3210 Series" x3).
// Empty string clears the label and falls back to printer_name.
export async function renamePrinter(id: string, label: string) {
  const { data } = await api.patch<PrinterRow>(`/printers/${id}/rename`, { label });
  return data;
}

// Test print — fire a one-page smoke test at a shared printer. Returns a
// task ID the caller polls until terminal. The task's result payload tells
// you what actually happened at the printer (printed, paper-out, offline,
// no-such-printer) — much richer than the legacy "we sent it to the spooler"
// fire-and-forget.
export type TestPrintTicket = {
  task_id: string;
  status: string;
  printer_id: string;
};

export type TestPrintStatus = {
  task_id: string;
  status: "pending" | "running" | "succeeded" | "failed" | "expired" | "cancelled";
  result: {
    outcome?: "ok" | "warn" | "error" | "not_found" | "unsupported" | "in_flight" | "unknown";
    detail?: string;
    verified?: boolean;
    job_status?: string;
    printed_at?: string;
    printer?: string;
    // print_file flow only — echoes the original filename so the UI
    // can show "Printed Report.pdf" instead of just "Printed".
    filename?: string;
    // reset_printer_settings flow only.
    reinstalled?: boolean;
    driver?: string;
    port?: string;
    user_reset_ok?: boolean;
    user_reset_note?: string;
    // save_card_settings flow only.
    saved?: boolean;
  } & CardSettingsInfo;
  error_message: string;
  created_at: string;
  completed_at: string | null;
};

export async function testPrintPrinter(id: string) {
  const { data } = await api.post<TestPrintTicket>(`/printers/${id}/test-print`);
  return data;
}

export async function getTestPrintStatus(taskId: string) {
  const { data } = await api.get<TestPrintStatus>(`/printers/test-prints/${taskId}`);
  return data;
}

// Cloud-routed file print. Uploads `file` to the dashboard, which
// creates a print_file task on the host agent. Same task pipeline as
// test print — poll getPrintFileStatus until terminal for the verified
// outcome.
//
// Why this exists alongside the local-UI drag-and-drop peer print:
// peer-to-peer requires LAN reachability between PCs, which is
// unreliable on real customer networks. The cloud route works on any
// network topology because both PCs only need to reach the dashboard.
export type PrintFileTicket = {
  task_id: string;
  status: string;
  printer_id: string;
  filename: string;
};

export async function printFileViaCloud(id: string, file: File, copies = 1, options: PrintOptions = {}) {
  const form = new FormData();
  form.append("file", file);
  form.append("copies", String(copies));
  for (const [key, value] of Object.entries(options)) {
    if (value) form.append(key, value);
  }
  const { data } = await api.post<PrintFileTicket>(`/printers/${id}/print-file`, form, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return data;
}

// Polling shape is identical to test print — same TestPrintStatus type
// works because the backend's TestPrintTaskStatusView accepts both
// task types. The extra fields below (`filename`, `printer`) are
// already on the type.
export async function getPrintFileStatus(taskId: string) {
  const { data } = await api.get<TestPrintStatus>(`/printers/print-files/${taskId}`);
  return data;
}

// One-click self-serve fix for a printer whose paper sizes (or other
// cached Windows/Office print settings) went stuck — clears the host PC's
// cached print defaults and reinstalls the printer queue fresh with the
// SAME driver/port it already had. Purely local to the host PC; doesn't
// require the printer to be shared with the office first.
export type ResetSettingsTicket = {
  task_id: string;
  status: string;
  printer_id: string;
};

export async function resetPrinterSettings(id: string) {
  const { data } = await api.post<ResetSettingsTicket>(`/printers/${id}/reset-settings`);
  return data;
}

// Same polling endpoint/shape as test print — see TestPrintStatus's
// reset_printer_settings-only fields (reinstalled, driver, port,
// user_reset_ok, user_reset_note).
export async function getResetPrinterSettingsStatus(taskId: string) {
  const { data } = await api.get<TestPrintStatus>(`/printers/test-prints/${taskId}`);
  return data;
}

// Save the card printer's current settings (read on its own PC) as the
// office's settings: every card printed through KraetdNet, from any PC,
// then uses them. Poll getTestPrintStatus with the returned task id.
export async function saveCardSettings(id: string) {
  const { data } = await api.post<TestPrintTicket>(`/printers/${id}/card-settings`);
  return data;
}

// Stop using the saved card settings (back to the printer's own settings).
export async function clearCardSettings(id: string) {
  await api.delete(`/printers/${id}/card-settings`);
}
